import React from 'react';
import {
  FaArrowLeft,
  FaDatabase,
  FaFileAlt,
  FaLock,
  FaUsers,
  FaUserShield,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import '../styles/AdminLegalPages.css';

export default function DataUsagePolicy() {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <header className="legal-header">
        <button
          type="button"
          className="legal-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <FaArrowLeft />
        </button>
        <div className="legal-header-content">
          <h1>Data Privacy Policy</h1>
          <p>How we collect, use, and protect your information.</p>
        </div>
      </header>

      <main className="legal-content">
        <section className="legal-section">
          <h2 className="legal-section-title">
            <FaDatabase /> Information We Collect
          </h2>
          <div className="legal-section-body">
            <p className="legal-section-intro">
              We collect the following types of information to provide you with our workforce management services:
            </p>
            <ul className="legal-list">
              <li><strong>Personal details</strong> - Name, phone number, email address, and contact information.</li>
              <li><strong>Employment information</strong> - Role, department, work assignments, and schedule.</li>
              <li><strong>Location during work shifts</strong> - GPS tracking for attendance and job site verification.</li>
              <li><strong>Device and login information</strong> - Device type, IP address, and session logs.</li>
              <li><strong>Uploaded documents</strong> - Work reports, photos, timesheets, and certifications.</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2 className="legal-section-title">
            <FaFileAlt /> How We Use Your Data
          </h2>
          <div className="legal-section-body">
            <p className="legal-section-intro">
              Your data is used exclusively to operate and improve our workforce management platform:
            </p>
            <ul className="legal-list">
              <li><strong>Project assignment</strong> - Matching contractors with appropriate jobs and tasks.</li>
              <li><strong>Communication between teams</strong> - Facilitating messages and updates between contractors and managers.</li>
              <li><strong>Payment processing</strong> - Calculating wages, tracking hours, and managing payroll.</li>
              <li><strong>Compliance and safety tracking</strong> - Ensuring workplace safety standards and regulatory compliance.</li>
              <li><strong>Platform improvement</strong> - Analyzing usage patterns to enhance features and user experience.</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2 className="legal-section-title">
            <FaLock /> Data Security
          </h2>
          <div className="legal-section-body">
            <p className="legal-section-intro">
              We take your data security seriously and implement multiple layers of protection:
            </p>
            <ul className="legal-list">
              <li><strong>Secure login authentication</strong> - Password encryption and session management.</li>
              <li><strong>Encrypted data storage</strong> - All sensitive data is encrypted at rest and in transit.</li>
              <li><strong>Role-based access control</strong> - Users can only access data relevant to their role.</li>
              <li><strong>Two-factor authentication</strong> - Optional additional security layer for your account.</li>
            </ul>
            <div className="legal-note success">
              <strong>Your data is protected</strong> with industry-standard security measures including SSL encryption and regular security audits.
            </div>
          </div>
        </section>

        <section className="legal-section">
          <h2 className="legal-section-title">
            <FaUsers /> Data Sharing
          </h2>
          <div className="legal-section-body">
            <p className="legal-section-intro">
              Your information is accessible only to authorized personnel within the system:
            </p>
            <ul className="legal-list">
              <li><strong>Super Admin</strong> - Full system access for platform administration.</li>
              <li><strong>Admin</strong> - Organizational management and workforce oversight.</li>
              <li><strong>Project Manager</strong> - Access to assigned project team data only.</li>
              <li><strong>Payroll system</strong> - Financial and employment data for payment processing.</li>
            </ul>
            <div className="legal-note">
              <strong>Important:</strong> User data is never shared with third parties without explicit consent.
            </div>
          </div>
        </section>

        <section className="legal-section">
          <h2 className="legal-section-title">
            <FaUserShield /> Your Rights
          </h2>
          <div className="legal-section-body">
            <p className="legal-section-intro">
              You have the following rights regarding your personal data:
            </p>
            <ul className="legal-list">
              <li><strong>Request data access</strong> - View all personal information we have stored about you.</li>
              <li><strong>Request data correction</strong> - Update or correct inaccurate information.</li>
              <li><strong>Request data deletion</strong> - Remove your data from our systems (subject to legal requirements).</li>
              <li><strong>Export personal data</strong> - Download a copy of your information in a portable format.</li>
            </ul>
          </div>
        </section>

        <button type="button" className="legal-footer-btn">
          Contact Support About Privacy
        </button>
      </main>
    </div>
  );
}
